import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, GraduationCap, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Link } from "react-router-dom";

export default function TrainingSidebar() {
  const { user } = useAuth();

  // Fetch next upcoming training session
  const { data: nextSession } = useQuery({
    queryKey: ["training-sidebar-next", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("*, training_groups(name), courts(name)")
        .gte("date", new Date().toISOString().split("T")[0])
        .order("date", { ascending: true })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id,
  });

  // Training stats
  const { data: trainingStats } = useQuery({
    queryKey: ["training-sidebar-stats", user?.id],
    queryFn: async () => {
      const { count: totalSessions } = await supabase
        .from("session_attendees")
        .select("*", { count: "exact", head: true })
        .eq("player_id", user!.id)
        .eq("status", "attended");

      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const { count: thisMonth } = await supabase
        .from("session_attendees")
        .select("*, training_sessions!inner(date)", { count: "exact", head: true })
        .eq("player_id", user!.id)
        .eq("status", "attended")
        .gte("training_sessions.date", monthStart);

      return {
        totalSessions: totalSessions ?? 0,
        thisMonth: thisMonth ?? 0,
      };
    },
    enabled: !!user?.id,
  });

  return (
    <div className="space-y-4">
      {/* Next session */}
      {nextSession && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Nasta pass
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-sm font-medium">
              {(nextSession as any).training_groups?.name}
            </p>
            <p className="text-xs text-muted-foreground">
              {format(new Date(nextSession.date), "EEEE d MMM", { locale: sv })}
            </p>
            <p className="text-xs text-muted-foreground">
              {nextSession.start_time?.slice(0, 5)} - {nextSession.end_time?.slice(0, 5)}
            </p>
            {(nextSession as any).courts?.name && (
              <p className="text-xs text-muted-foreground">
                {(nextSession as any).courts.name}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Training stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Träningsstatistik
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Totalt antal pass</span>
            <span className="text-sm font-medium">{trainingStats?.totalSessions ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Denna manad</span>
            <span className="text-sm font-medium">{trainingStats?.thisMonth ?? 0}</span>
          </div>
        </CardContent>
      </Card>

      {/* Find training */}
      <Card>
        <CardContent className="p-3 space-y-2">
          <Button variant="outline" size="sm" className="w-full gap-1 text-xs" asChild>
            <Link to="/play?type=training">
              <GraduationCap className="h-3.5 w-3.5" />
              Hitta träningspass
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
